import { useMemo, useState, type ReactNode } from 'react';
import {
  Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, FormControlLabel, InputAdornment, Stack, TextField, Typography,
  createFilterOptions,
} from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { destinationsApi, type NamedRef } from '@/api/places.api';
import {
  KYC_LABEL, LIST_KEY, VENDOR_STATUS_LABEL, vendorListsApi, vendorsApi, type KycStatus, type ListItem, type ListKind, type Slab, type VendorStatus,
} from '@/api/vendors.api';
import { useNotify } from '@/components/common/useNotify';

// ---------------------------------------------------------------------------------------------------------------
// Chips: status (Active green, Draft amber, Pending approval blue, Inactive grey, Blocked red) and KYC (V1).
// ---------------------------------------------------------------------------------------------------------------

const STATUS_SX: Record<VendorStatus, { bg: string; fg: string }> = {
  Active: { bg: '#E7F4EC', fg: '#1E6B3A' },
  Draft: { bg: '#FDF1DC', fg: '#8A5A00' },
  PendingApproval: { bg: '#E6EEF9', fg: '#1D4E89' },
  Inactive: { bg: '#EEF0F3', fg: '#4B5563' },
  Blocked: { bg: '#FCEFEF', fg: '#B42318' },
};

export function VendorStatusChip({ status }: { status: VendorStatus }) {
  const s = STATUS_SX[status] ?? STATUS_SX.Draft;
  return <Chip size="small" label={VENDOR_STATUS_LABEL[status] ?? status} sx={{ bgcolor: s.bg, color: s.fg, fontWeight: 600, height: 22 }} />;
}

export function KycChip({ status, label }: { status: KycStatus; label?: string }) {
  const ok = status === 'Approved';
  const bad = status === 'Rejected';
  return (
    <Chip size="small" label={label ?? KYC_LABEL[status]}
      sx={{ bgcolor: ok ? '#E7F4EC' : bad ? '#FCEFEF' : '#FDF1DC', color: ok ? '#1E6B3A' : bad ? '#B42318' : '#8A5A00', fontWeight: 600, height: 22 }} />
  );
}

export function SupplyChips({ supplies }: { supplies: string[] }) {
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
      {supplies.map((s) => <Chip key={s} size="small" label={s} sx={{ bgcolor: '#E6EEF9', color: '#1D4E89', fontWeight: 600, height: 22 }} />)}
    </Stack>
  );
}

/** The "From GST" / "From IFSC" / "From OCR" label beside a field (M2.0 "Where data is fetched"). */
export function FromTag({ source }: { source: 'GST' | 'IFSC' | 'OCR' }) {
  return <Chip size="small" label={`From ${source}`} sx={{ height: 18, fontSize: 11, bgcolor: '#E7F4EC', color: '#1E6B3A', fontWeight: 600 }} />;
}

export function Label({ text, required, tag, children }: { text: string; required?: boolean; tag?: ReactNode; children?: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, minHeight: 22 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{text}{required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}</Typography>
      {tag}
      {children}
    </Stack>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// The lists behind the dropdowns (M2.11), each ending with "+ Add new" (FR-UI-05).
// ---------------------------------------------------------------------------------------------------------------

export function useVendorList(kind: ListKind, enabled = true) {
  return useQuery({ queryKey: [LIST_KEY, kind], queryFn: () => vendorListsApi.items(kind), staleTime: 300_000, enabled });
}

const ONE: Record<ListKind, string> = {
  SupplyType: 'supply type', BusinessType: 'business type', ContactDepartment: 'department', PaymentTerm: 'payment term',
  CreditPeriod: 'credit period', CancellationTemplate: 'cancellation template', RbiPurposeCode: 'purpose code', KycDocRule: 'document rule',
  BankAccountType: 'account type',
  HotelCategory: 'hotel category', HotelTypeTag: 'type tag', BedType: 'bed type', RoomView: 'view', MealPlan: 'meal plan',
};

const ADD = '__add__';
const key = (s: string) => s.trim().replace(/\s+/g, ' ').toUpperCase();

/** Dropdown of a list's active values with "+ Add new" at the end; the pop-up blocks a name that exists (FR-UI-06). */
export function ListSelect({ kind, value, onChange, id, error, helperText, disabled, placeholder }: {
  kind: ListKind; value: string | null | undefined; onChange: (v: string | null) => void; id?: string; error?: boolean;
  helperText?: ReactNode; disabled?: boolean; placeholder?: string;
}) {
  const list = useVendorList(kind);
  const [adding, setAdding] = useState(false);
  const names = useMemo(() => (list.data ?? []).filter((i) => i.isActive || i.name === value).map((i) => i.name), [list.data, value]);
  return (
    <>
      <Autocomplete size="small" id={id} options={[...names, ADD]} value={value ?? null} disabled={disabled} loading={list.isLoading}
        onChange={(_, v) => { if (v === ADD) setAdding(true); else onChange(v); }}
        getOptionLabel={(o) => (o === ADD ? `+ Add new ${ONE[kind]}` : o)}
        renderOption={(props, o) => {
          const { key: k, ...rest } = props as typeof props & { key: string };
          return <li key={k} {...rest} style={o === ADD ? { color: '#1D4E89', fontWeight: 600, borderTop: '1px solid #E5E9F0' } : undefined}>{o === ADD ? '+ Add new' : o}</li>;
        }}
        renderInput={(p) => <TextField {...p} placeholder={placeholder} error={error} helperText={helperText} />} />
      {adding && <QuickAddDialog kind={kind} existing={list.data ?? []} onClose={() => setAdding(false)} onAdded={(n) => { setAdding(false); onChange(n); }} />}
    </>
  );
}

/** As ListSelect, but the value is the list row's id (the hotel category is kept by id, M3.8). */
export function ListIdSelect({ kind, value, onChange, id, error, helperText, disabled, placeholder }: {
  kind: ListKind; value: number | null | undefined; onChange: (v: number | null) => void; id?: string; error?: boolean;
  helperText?: ReactNode; disabled?: boolean; placeholder?: string;
}) {
  const list = useVendorList(kind);
  const [adding, setAdding] = useState(false);
  const ids = useMemo(() => (list.data ?? []).filter((i) => i.isActive || i.id === value).map((i) => i.id), [list.data, value]);
  const nameOf = (o: number) => (list.data ?? []).find((i) => i.id === o)?.name ?? '';
  const ADD_ID = -1;
  return (
    <>
      <Autocomplete size="small" id={id} options={[...ids, ADD_ID]} value={value ?? null} disabled={disabled} loading={list.isLoading}
        onChange={(_, v) => { if (v === ADD_ID) setAdding(true); else onChange(v); }}
        getOptionLabel={(o) => (o === ADD_ID ? `+ Add new ${ONE[kind]}` : nameOf(o))}
        renderOption={(props, o) => {
          const { key: k, ...rest } = props as typeof props & { key: string };
          return <li key={k} {...rest} style={o === ADD_ID ? { color: '#1D4E89', fontWeight: 600, borderTop: '1px solid #E5E9F0' } : undefined}>{o === ADD_ID ? '+ Add new' : nameOf(o)}</li>;
        }}
        renderInput={(p) => <TextField {...p} placeholder={placeholder} error={error} helperText={helperText} />} />
      {adding && <QuickAddDialog kind={kind} existing={list.data ?? []} onClose={() => setAdding(false)}
        onAdded={(_, addedId) => { setAdding(false); if (addedId) onChange(addedId); }} />}
    </>
  );
}

/** "+ Add new" pop-up for a vendor list; a supply type also asks for its two-letter ID prefix. */
export function QuickAddDialog({ kind, existing, onClose, onAdded }: {
  kind: ListKind; existing: ListItem[]; onClose: () => void;
  /** The name added (or the existing one selected) and its id. */
  onAdded: (name: string, id?: number) => void;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const same = name.trim() ? existing.find((e) => key(e.name) === key(name)) : undefined;
  const prefixTaken = kind === 'SupplyType' && code.length === 2 ? existing.find((e) => (e.code ?? '').toUpperCase() === code.toUpperCase()) : undefined;
  const add = useMutation({
    mutationFn: () => vendorListsApi.quickAdd(kind, {
      name: name.trim(), code: kind === 'SupplyType' || kind === 'MealPlan' ? code.toUpperCase() : null, isActive: true,
    }),
    onSuccess: async (row) => { await qc.invalidateQueries({ queryKey: [LIST_KEY, kind] }); notify.success(`'${row.name}' added.`); onAdded(row.name, row.id); },
    onError: (e) => notify.error(e),
  });
  const codeTaken = kind === 'MealPlan' && code ? existing.find((e) => (e.code ?? '').toUpperCase() === code.toUpperCase()) : undefined;
  const valid = name.trim().length > 0 && !same && (kind !== 'SupplyType' || (/^[A-Za-z]{2}$/.test(code) && !prefixTaken))
    && (kind !== 'MealPlan' || (/^[A-Za-z0-9]{1,10}$/.test(code) && !codeTaken));
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pt: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#FCEFEF', color: 'error.main', display: 'grid', placeItems: 'center', flexShrink: 0 }}><AddRounded /></Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={700} sx={{ mb: 1.5 }}>Add new {ONE[kind]}</Typography>
            <Label text="Name" required />
            <TextField fullWidth size="small" autoFocus value={name} onChange={(e) => setName(e.target.value)} error={!!same}
              helperText={same ? `'${same.name}' already exists, so it cannot be added again. Select the existing one.` : ' '} />
            {same && <Button variant="outlined" size="small" sx={{ mb: 1.5 }} onClick={() => onAdded(same.name, same.id)}>Select '{same.name}'</Button>}
            {kind === 'MealPlan' && (
              <>
                <Label text="Code" required />
                <TextField size="small" value={code} onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 10).toUpperCase())}
                  error={!!codeTaken} helperText={codeTaken ? `${code} is used by ${codeTaken.name}.` : 'Short code shown in rate tables, e.g. HB.'} />
              </>
            )}
            {kind === 'SupplyType' && (
              <>
                <Label text="ID prefix" required />
                <TextField size="small" value={code} onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase())}
                  error={!!prefixTaken} helperText={prefixTaken ? `${code} is used by ${prefixTaken.name}.` : 'Two letters; vendors whose first supply is this get IDs like HB1.'} />
              </>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || add.isPending} onClick={() => add.mutate()}>{add.isPending ? 'Adding…' : 'Add and select'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Tag field of destinations (V2: "Type a destination and press Enter to add it; add as many as needed").
// ---------------------------------------------------------------------------------------------------------------

const destFilter = createFilterOptions<NamedRef>({ stringify: (o) => `${o.name} ${o.code}` });

export function DestinationTags({ value, onChange, id, error, helperText, placeholder, limitTo }: {
  value: number[]; onChange: (ids: number[]) => void; id?: string; error?: boolean; helperText?: ReactNode; placeholder?: string;
  /** Offer only these destinations (preferred must be among those served). */
  limitTo?: number[];
}) {
  const dests = useQuery({ queryKey: ['destination-options', 'all'], queryFn: () => destinationsApi.options(), staleTime: 300_000 });
  const all = dests.data ?? [];
  const options = limitTo ? all.filter((d) => limitTo.includes(d.id)) : all;
  const picked = value.map((v) => all.find((d) => d.id === v)).filter((d): d is NamedRef => !!d);
  return (
    <Autocomplete multiple size="small" id={id} options={options} value={picked} loading={dests.isLoading} filterSelectedOptions
      isOptionEqualToValue={(a, b) => a.id === b.id} filterOptions={destFilter}
      getOptionLabel={(o) => title(o.name)}
      renderOption={(props, o) => { const { key: k, ...rest } = props as typeof props & { key: string }; return <li key={k} {...rest}>{title(o.name)} <Box component="span" sx={{ color: 'text.secondary', ml: 0.75, fontSize: 12 }}>{o.code}</Box></li>; }}
      onChange={(_, v) => onChange(v.map((d) => d.id))}
      renderTags={(v, getTagProps) => v.map((d, i) => { const { key: k, ...rest } = getTagProps({ index: i }); return <Chip key={k} {...rest} size="small" label={title(d.name)} sx={{ bgcolor: '#E6EEF9', color: '#1D4E89', fontWeight: 600 }} />; })}
      renderInput={(p) => <TextField {...p} placeholder={value.length ? '' : placeholder} error={error} helperText={helperText} />} />
  );
}

/** "MANALI" → "Manali" (names are stored in capitals; tags read like the design). */
export function title(name: string) {
  return name.split(' ').filter(Boolean).map((w) => (w.length <= 1 ? w : w[0] + w.slice(1).toLowerCase())).join(' ');
}

/** "₹10,000" with Indian grouping. */
export function rupees(n: number) {
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Cancellation slabs (V8, cancellation policy templates).
// ---------------------------------------------------------------------------------------------------------------

const FieldMsg = ({ text }: { text?: string }) => (text ? <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>{text}</Box> : null);

export function slabLabel(s: Slab) {
  if (s.isNoShow) return 'No-show';
  if (s.daysTo == null) return `${s.daysFrom} days or more before arrival`;
  return `${s.daysFrom} – ${s.daysTo} days`;
}

export function SlabDialog({ row, onClose, onSave }: { row: Slab; onClose: () => void; onSave: (r: Slab) => void }) {
  const [r, setR] = useState<Slab>(row);
  const [orMore, setOrMore] = useState(row.daysTo == null && !row.isNoShow);
  const bad = !r.isNoShow && (r.daysFrom < 0 || (!orMore && (r.daysTo == null || r.daysTo < r.daysFrom)));
  const badPct = r.chargePct < 0 || r.chargePct > 100;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pt: 3 }}>
        <Typography fontWeight={700} sx={{ mb: 2 }}>{row.chargePct || row.daysFrom ? 'Edit slab' : 'Add slab'}</Typography>
        <FormControlLabel control={<Checkbox size="small" checked={r.isNoShow} onChange={(e) => setR({ ...r, isNoShow: e.target.checked })} />} label="No-show" />
        {!r.isNoShow && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1.5 }}>
            <TextField size="small" type="number" label="From (days before)" value={r.daysFrom} onChange={(e) => setR({ ...r, daysFrom: Number(e.target.value) })} error={bad} />
            {!orMore && <TextField size="small" type="number" label="To (days before)" value={r.daysTo ?? ''} onChange={(e) => setR({ ...r, daysTo: e.target.value === '' ? null : Number(e.target.value) })} error={bad} />}
          </Stack>
        )}
        {!r.isNoShow && <FormControlLabel control={<Checkbox size="small" checked={orMore} onChange={(e) => { setOrMore(e.target.checked); if (e.target.checked) setR({ ...r, daysTo: null }); }} />} label="or more days" />}
        {bad && <FieldMsg text="The first number of days must not be more than the second." />}
        <TextField fullWidth size="small" type="number" label="Charge (% of booking value)" value={r.chargePct} onChange={(e) => setR({ ...r, chargePct: Number(e.target.value) })}
          error={badPct} helperText={badPct ? 'Between 0 and 100.' : ' '} sx={{ mt: 2 }} slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={bad || badPct} onClick={() => onSave({ ...r, daysTo: r.isNoShow || orMore ? null : r.daysTo, daysFrom: r.isNoShow ? 0 : r.daysFrom })}>Save slab</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// A vendor picker for other masters (e.g. the supplier of a sub-tour).
// ---------------------------------------------------------------------------------------------------------------

export function VendorSelect({ value, onChange, supply, disabled }: { value: number | null; onChange: (id: number | null) => void; supply?: string; disabled?: boolean }) {
  const list = useQuery({ queryKey: ['vendors', 'options', supply ?? ''], queryFn: () => vendorsApi.options({ supply }), staleTime: 60_000 });
  const options = list.data ?? [];
  return (
    <Autocomplete size="small" options={options} value={options.find((o) => o.id === value) ?? null} disabled={disabled} loading={list.isLoading}
      isOptionEqualToValue={(a, c) => a.id === c.id} getOptionLabel={(o) => `${o.name} (${o.code})`} onChange={(_, v) => onChange(v?.id ?? null)}
      renderInput={(p) => <TextField {...p} placeholder="Choose the vendor" />} />
  );
}
